/**
 * @module CanvasResultsArea
 * @purpose Central content area housing search bar, attribute chart, and results table.
 * @depends DecoratedArtefact, CanvasViewMode (local types), FacetEntry (analysis-artefacts types)
 * @dependedOnBy CanvasView
 *
 * @rules
 * - Performance: delegates virtualisation to CanvasResultsTable
 * - State: none — all state owned by CanvasView / useCanvasState
 * - Errors: n/a — parent handles loading/error/empty states above this component
 */

import { useTranslation } from 'react-i18next'
import { CanvasSearchBar } from './CanvasSearchBar'
import { CanvasViewToggle } from './CanvasViewToggle'
import { CanvasAttributeChart } from './CanvasAttributeChart'
import { CanvasResultsTable } from './CanvasResultsTable'
import { ReasoningCtaButton } from './reasoning/ReasoningCtaButton'
import type { DecoratedArtefact, CanvasViewMode } from './types'
import type { FacetEntry, AnalysisFilters } from '../../../types/analysis-artefacts'

interface CanvasResultsAreaProps {
  caseId: string
  artefacts: DecoratedArtefact[]
  artefactsLoading: boolean
  searchQuery: string
  activeView: CanvasViewMode
  selectedArtefactId: string | null
  attributeData: FacetEntry[]
  selectedFilters: AnalysisFilters
  reasoningPanelOpen: boolean
  onSearchChange: (query: string) => void
  onViewChange: (view: CanvasViewMode) => void
  onRowSelect: (id: string | null) => void
  onBookmark: (id: string) => void
  onStar: (id: string) => void
  onTagAction: (id: string) => void
  onReasoningToggle: (open: boolean) => void
}

/**
 * Results area with toolbar and table.
 *
 * @behaviour Passes attributeData to CanvasAttributeChart for the stacked type bar.
 *            Delegates row interactions to parent callbacks.
 * @errors n/a
 */
export function CanvasResultsArea({
  caseId,
  artefacts,
  artefactsLoading,
  searchQuery,
  activeView,
  selectedArtefactId,
  attributeData,
  selectedFilters,
  reasoningPanelOpen,
  onSearchChange,
  onViewChange,
  onRowSelect,
  onBookmark,
  onStar,
  onTagAction,
  onReasoningToggle,
}: CanvasResultsAreaProps): React.ReactElement {
  const { t } = useTranslation('analysis')
  void t
  return (
    <div className="flex h-full flex-col overflow-hidden">
      <div
        className="flex items-center gap-3 border-b px-3 py-2"
        style={{ borderColor: 'var(--border)' }}
      >
        <div className="flex-1">
          <CanvasSearchBar value={searchQuery} onChange={onSearchChange} />
        </div>
        <ReasoningCtaButton
          caseId={caseId}
          artefactCount={artefacts.length}
          artefactsLoading={artefactsLoading}
          selectedFilters={selectedFilters}
          reasoningPanelOpen={reasoningPanelOpen}
          onToggle={onReasoningToggle}
        />
        <CanvasViewToggle activeView={activeView} onSetView={onViewChange} />
      </div>
      <div className="px-3 py-2">
        <CanvasAttributeChart attributeData={attributeData} />
      </div>
      <div className="flex-1 overflow-y-auto">
        <CanvasResultsTable
          artefacts={artefacts}
          selectedArtefactId={selectedArtefactId}
          onSelectArtefact={onRowSelect}
          onBookmark={onBookmark}
          onStar={onStar}
          onTag={onTagAction}
        />
      </div>
    </div>
  )
}
